import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { money } from "@/lib/format";
import { Alert, Badge, Button, Card, EmptyState, Field, PageHeader, Select, Skeleton, cx } from "@/components/ui";
import type { ServiceLevel } from "./ServiceLevels";

type MatchType = "suburb" | "city" | "postal_code";
type AppliesTo = "collection" | "delivery" | "either";
type SurchargeKind = "fixed" | "percent";

interface ZoneArea {
  id: string;
  match_type: MatchType;
  match_value: string;
}

interface ZoneSurcharge {
  id: string;
  service_level_id?: string;
  applies_to: AppliesTo;
  kind: SurchargeKind;
  amount: number;
  label?: string;
}

interface RateZone {
  id: string;
  name: string;
  active: boolean;
  areas?: ZoneArea[];
  surcharges?: ZoneSurcharge[];
}

const MATCH_LABELS: Record<MatchType, string> = { suburb: "Suburb", city: "City", postal_code: "Postal code" };
const APPLIES_LABELS: Record<AppliesTo, string> = { collection: "Collection", delivery: "Delivery", either: "Either leg" };

function surchargeLabel(sc: ZoneSurcharge, levels: ServiceLevel[]): string {
  const amount = sc.kind === "percent" ? `${(sc.amount / 100).toFixed(2)}%` : money(sc.amount);
  const level = sc.service_level_id ? levels.find((l) => l.id === sc.service_level_id)?.name ?? "Unknown level" : "All levels";
  return `${amount} on ${APPLIES_LABELS[sc.applies_to]} -- ${level}`;
}

/**
 * Zones an admin can attach a surcharge to -- "High risk", "Hospital
 * precinct", whatever the business calls it -- matched on suburb, city or
 * postal code rather than a drawn boundary. See
 * internal/courier/fleet.go's zoneSurchargeCents for how a route picks up
 * every zone that matches either leg.
 */
export function RateZones() {
  const queryClient = useQueryClient();
  const [newZoneName, setNewZoneName] = useState("");
  const [error, setError] = useState("");

  const { data: zonesData, isLoading } = useQuery({
    queryKey: ["rate-zones"],
    queryFn: () => api.get<{ data: RateZone[] }>("/v1/admin/rates/zones"),
  });
  const { data: levelsData } = useQuery({
    queryKey: ["service-levels"],
    queryFn: () => api.get<{ data: ServiceLevel[] }>("/v1/admin/rates/service-levels"),
  });
  const levels = levelsData?.data ?? [];

  const invalidateZones = () => queryClient.invalidateQueries({ queryKey: ["rate-zones"] });

  const createZone = useMutation({
    mutationFn: () => api.post<RateZone>("/v1/admin/rates/zones", { name: newZoneName.trim() }),
    onSuccess: () => {
      setNewZoneName("");
      setError("");
      invalidateZones();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not create the zone."),
  });

  const toggleZone = useMutation({
    mutationFn: (zone: RateZone) => api.put<void>(`/v1/admin/rates/zones/${zone.id}`, { name: zone.name, active: !zone.active }),
    onSuccess: invalidateZones,
  });

  const removeZone = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/admin/rates/zones/${id}`),
    onSuccess: invalidateZones,
  });

  return (
    <>
      <PageHeader title="Zones & surcharges" subtitle="Extra cost layered on top of a service level's base+distance price for a route touching a named area." />

      <Card className="mb-5">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">Add a zone</h2>
        {error && (
          <div className="mb-4">
            <Alert>{error}</Alert>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label="Name"
            className="flex-1"
            value={newZoneName}
            onChange={(e) => setNewZoneName(e.target.value)}
            placeholder="High risk"
          />
          <Button loading={createZone.isPending} disabled={!newZoneName.trim()} onClick={() => createZone.mutate()}>
            Add zone
          </Button>
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : !zonesData?.data.length ? (
        <EmptyState title="No zones yet" body="Add one above, then attach areas (suburb, city or postal code) and surcharges to it." />
      ) : (
        <div className="space-y-4">
          {zonesData.data.map((zone) => (
            <ZoneCard
              key={zone.id}
              zone={zone}
              levels={levels}
              onToggleActive={() => toggleZone.mutate(zone)}
              onRemove={() => removeZone.mutate(zone.id)}
              onChanged={invalidateZones}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ZoneCard({
  zone,
  levels,
  onToggleActive,
  onRemove,
  onChanged,
}: {
  zone: RateZone;
  levels: ServiceLevel[];
  onToggleActive: () => void;
  onRemove: () => void;
  onChanged: () => void;
}) {
  const [matchType, setMatchType] = useState<MatchType>("suburb");
  const [matchValue, setMatchValue] = useState("");
  const [surchargeLevelId, setSurchargeLevelId] = useState("");
  const [appliesTo, setAppliesTo] = useState<AppliesTo>("either");
  const [kind, setKind] = useState<SurchargeKind>("fixed");
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");

  const addArea = useMutation({
    mutationFn: () =>
      api.post<ZoneArea>(`/v1/admin/rates/zones/${zone.id}/areas`, { match_type: matchType, match_value: matchValue.trim() }),
    onSuccess: () => {
      setMatchValue("");
      onChanged();
    },
  });
  const removeArea = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/admin/rates/zone-areas/${id}`),
    onSuccess: onChanged,
  });

  const addSurcharge = useMutation({
    mutationFn: () =>
      api.post<ZoneSurcharge>(`/v1/admin/rates/zones/${zone.id}/surcharges`, {
        service_level_id: surchargeLevelId || undefined,
        applies_to: appliesTo,
        kind,
        // Fixed: Rands -> cents. Percent: e.g. "10" (10.00%) -> 1000 basis
        // points. Both are the same *100 -- see domain.RateZoneSurcharge.
        amount: Math.round(Number(amount || 0) * 100),
        label: label.trim(),
      }),
    onSuccess: () => {
      setAmount("");
      setLabel("");
      onChanged();
    },
  });
  const removeSurcharge = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/admin/rates/zone-surcharges/${id}`),
    onSuccess: onChanged,
  });

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold tracking-tight">{zone.name}</h3>
          {!zone.active && <Badge tone="bad">inactive</Badge>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={onToggleActive}>
            {zone.active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="danger" onClick={onRemove}>
            Remove zone
          </Button>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-sm font-medium text-ink-700">Areas</p>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {!zone.areas?.length && <p className="text-sm text-ink-400">No areas -- this zone matches nothing yet.</p>}
            {zone.areas?.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 rounded-lg bg-ink-100 px-2 py-1 text-xs">
                {MATCH_LABELS[a.match_type]}: {a.match_value}
                <button type="button" className="text-ink-400 hover:text-signal-bad" onClick={() => removeArea.mutate(a.id)}>
                  &times;
                </button>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Match"
              className="w-32"
              value={matchType}
              onChange={(e) => setMatchType(e.target.value as MatchType)}
              options={Object.entries(MATCH_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Field label="Value" className="flex-1" value={matchValue} onChange={(e) => setMatchValue(e.target.value)} placeholder="Sandton" />
            <Button size="sm" disabled={!matchValue.trim()} loading={addArea.isPending} onClick={() => addArea.mutate()}>
              Add
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-ink-700">Surcharges</p>
          <div className="mb-3 space-y-1.5">
            {!zone.surcharges?.length && <p className="text-sm text-ink-400">No surcharges on this zone.</p>}
            {zone.surcharges?.map((sc) => (
              <div key={sc.id} className="flex items-center justify-between gap-2 rounded-lg bg-ink-100 px-3 py-1.5 text-xs">
                <span>
                  {surchargeLabel(sc, levels)}
                  {sc.label ? ` (${sc.label})` : ""}
                </span>
                <button type="button" className="text-ink-400 hover:text-signal-bad" onClick={() => removeSurcharge.mutate(sc.id)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Service level"
              value={surchargeLevelId}
              onChange={(e) => setSurchargeLevelId(e.target.value)}
              options={[{ value: "", label: "All levels" }, ...levels.map((l) => ({ value: l.id, label: l.name }))]}
            />
            <Select
              label="Applies to"
              value={appliesTo}
              onChange={(e) => setAppliesTo(e.target.value as AppliesTo)}
              options={Object.entries(APPLIES_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              label="Kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as SurchargeKind)}
              options={[
                { value: "fixed", label: "Fixed (R)" },
                { value: "percent", label: "Percent (%)" },
              ]}
            />
            <Field
              label={kind === "percent" ? "Amount (%)" : "Amount (R)"}
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <Field label="Label (optional)" className="col-span-2" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="High risk fee" />
          </div>
          <Button
            size="sm"
            className={cx("mt-2")}
            disabled={!amount.trim()}
            loading={addSurcharge.isPending}
            onClick={() => addSurcharge.mutate()}
          >
            Add surcharge
          </Button>
        </div>
      </div>
    </Card>
  );
}
